import React, { useEffect, useRef, useState } from "react";

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  pulsePhase: number;
  connections: number[];
  /** Depth in [0,1] for the 3D fly-in parallax: 0 = far (slowest), 1 = near (fastest). */
  depth: number;
  /** Per-frame drawn position and zoom factor after depth parallax is applied. */
  dx: number;
  dy: number;
  dz: number;
}

interface NeuralBackgroundProps {
  theme?: "dark" | "light";
  nodeCount?: number;
  animationSpeed?: number;
  opacity?: number;
  className?: string;
  /** When flipped to true, the component drives its own 0→1 bloom animation
   *  inside the existing rAF loop — no React state updates, no re-renders. */
  transitioning?: boolean;
  /** When true, the network intensifies from its resting opacity toward a
   *  brighter "loading" state. Used to give the background presence during the
   *  post-login data wait (before the bloom). Ramped inside the rAF loop. */
  active?: boolean;
  /** Fired once when the bloom reaches 1.0 (screen is white). */
  onTransitionEnd?: () => void;
}

/** Duration of the bloom animation (ms). */
const TRANSITION_DURATION_MS = 2400;

/** Duration of the ambient "come alive" ramp when `active` flips true (ms). */
const ACTIVE_RAMP_MS = 700;
/** Opacity the network intensifies toward while active — brighter than the
 *  resting `opacity` prop so the background has presence during the post-login
 *  data wait, but well short of the bloom's full white-out. */
const ACTIVE_OPACITY = 0.5;
/** Active-state energy (at full `active`): how much faster nodes drift, how much
 *  faster they pulse, and how much larger they draw — so the network visibly
 *  "thinks harder" while loading, not merely brighter. */
const ACTIVE_MOTION_BOOST = 0.8;
const ACTIVE_PULSE_BOOST = 1.2;
const ACTIVE_NODE_SCALE = 0.35;

/** Charge-up (wind-up) before the bloom: a brief beat where the network
 *  brightens and the camera pushes forward (zooms in), so the bloom reads as
 *  arriving / entering the screen rather than an abrupt flash. */
const CHARGE_DURATION_MS = 500;
/** Opacity the network surges to during the charge-up, before any white. */
const CHARGE_OPACITY = 0.85;
/** Camera zoom-in ("dive into the network"). It begins the instant the network
 *  activates on login — it does NOT wait for the login card/overlay fade or the
 *  data load — easing (smoothstep) to ZOOM_MAX over ZOOM_DIVE_MS, then holding
 *  there until the bloom whites out. 1 = no zoom. */
const ZOOM_MAX = 2.4;
const ZOOM_DIVE_MS = 2600;
/** Per-node depth spread for the 3D parallax during the dive. Each node zooms by
 *  a factor between DEPTH_FAR (farthest — spreads/grows slowest) and DEPTH_NEAR
 *  (nearest — fastest), so the fly-in reads as moving *through* a 3D cloud rather
 *  than magnifying a flat image. Centered on ~1 so the average matches a plain zoom. */
const DEPTH_FAR = 0.55;
const DEPTH_NEAR = 1.6;

/** Linearly interpolate between two values. */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Convert "#rrggbb" to [r, g, b]. */
function hexToRgbTuple(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

/** Interpolate a hex color toward white by factor t (0–1), return "r, g, b". */
function lerpHexToWhiteRgb(hex: string, t: number): string {
  const [r, g, b] = hexToRgbTuple(hex);
  return `${Math.round(lerp(r, 255, t))}, ${Math.round(lerp(g, 255, t))}, ${Math.round(lerp(b, 255, t))}`;
}

/** Interpolate a hex color toward white by factor t (0–1), return "#rrggbb". */
function lerpHexToWhite(hex: string, t: number): string {
  const [r, g, b] = hexToRgbTuple(hex);
  const nr = Math.round(lerp(r, 255, t));
  const ng = Math.round(lerp(g, 255, t));
  const nb = Math.round(lerp(b, 255, t));
  return `#${nr.toString(16).padStart(2, "0")}${ng.toString(16).padStart(2, "0")}${nb.toString(16).padStart(2, "0")}`;
}

const NeuralNetworkBackground: React.FC<NeuralBackgroundProps> = ({
  theme = "dark",
  nodeCount = 50,
  animationSpeed = 1,
  opacity = 0.6,
  className = "",
  transitioning = false,
  active = false,
  onTransitionEnd,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const nodesRef = useRef<Node[]>([]);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Transition state — all in refs so the rAF loop reads them without re-renders.
  const transitioningRef = useRef(transitioning);
  const transitionProgressRef = useRef(0);
  const chargeProgressRef = useRef(0);
  const transitionStartTimeRef = useRef<number | null>(null);
  const transitionEndFiredRef = useRef(false);
  const onTransitionEndRef = useRef(onTransitionEnd);

  // Ambient "come alive" intensify — all in refs so the rAF loop reads them
  // without re-renders (a change to the `opacity` prop would re-init the nodes).
  const activeRef = useRef(active);
  const activeProgressRef = useRef(0);
  const activeStartTimeRef = useRef<number | null>(null);

  // Sync prop changes into refs (no re-renders triggered by the animation).
  transitioningRef.current = transitioning;
  activeRef.current = active;
  onTransitionEndRef.current = onTransitionEnd;

  const bgStops: Record<string, [string, string, string]> = {
    dark: ["#020c32", "#041447", "#020c32"],
    light: ["#f8fafc", "#e2e8f0", "#f1f5f9"],
  };

  const colorSets = {
    dark: {
      node: "#60a5fa",
      nodeGlow: "#93c5fd",
      pulse: "#bfdbfe",
    },
    light: {
      node: "#3b82f6",
      nodeGlow: "#60a5fa",
      pulse: "#93c5fd",
    },
  };

  const currentBgStops = bgStops[theme];
  const currentColors = colorSets[theme];

  // Initialize nodes
  const initializeNodes = (width: number, height: number): Node[] => {
    const nodes: Node[] = [];

    for (let i = 0; i < nodeCount; i++) {
      const startX = Math.random() * width;
      const startY = Math.random() * height;
      nodes.push({
        x: startX,
        y: startY,
        vx: (Math.random() - 0.5) * 0.3 * animationSpeed,
        vy: (Math.random() - 0.5) * 0.3 * animationSpeed,
        radius: Math.random() * 2 + 1.5,
        pulsePhase: Math.random() * Math.PI * 2,
        connections: [],
        depth: Math.random(),
        dx: startX,
        dy: startY,
        dz: 1,
      });
    }

    nodes.forEach((node, i) => {
      nodes.forEach((otherNode, j) => {
        if (i !== j) {
          const dx = node.x - otherNode.x;
          const dy = node.y - otherNode.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 180 && node.connections.length < 6) {
            node.connections.push(j);
          }
        }
      });
    });

    return nodes;
  };

  // Update canvas size
  const updateCanvasSize = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(dpr, dpr);
    }

    setDimensions({ width: rect.width, height: rect.height });
  };

  // Animation loop
  const animate = (timestamp: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const { width, height } = dimensions;

    // --- Drive the transition entirely inside the rAF loop, in two phases:
    //     (1) charge-up — the network brightens and the camera zooms in (a
    //         wind-up), no white yet; (2) bloom — the swell-to-white finale. ---
    if (transitioningRef.current) {
      if (transitionStartTimeRef.current === null) {
        transitionStartTimeRef.current = timestamp;
      }
      const elapsed = timestamp - transitionStartTimeRef.current;

      // Phase 1 — charge-up ramp (ease-out). Drives opacity only, not the zoom
      // (the zoom is reveal-driven — see `zoom` below).
      const rawCharge = Math.min(elapsed / CHARGE_DURATION_MS, 1);
      chargeProgressRef.current = 1 - Math.pow(1 - rawCharge, 3);

      // Phase 2 — bloom ramp (ease-in-out), begins once the charge completes.
      if (elapsed > CHARGE_DURATION_MS && transitionProgressRef.current < 1) {
        const bloomElapsed = elapsed - CHARGE_DURATION_MS;
        const raw = Math.min(bloomElapsed / TRANSITION_DURATION_MS, 1);
        transitionProgressRef.current =
          raw < 0.5 ? 2 * raw * raw : 1 - Math.pow(-2 * raw + 2, 2) / 2;

        if (raw >= 1 && !transitionEndFiredRef.current) {
          transitionEndFiredRef.current = true;
          onTransitionEndRef.current?.();
        }
      }
    }

    // --- Ambient "come alive" ramp: while `active` (post-login, before the
    //     bloom) the network intensifies from its resting opacity toward
    //     ACTIVE_OPACITY, giving the background presence during the data wait.
    //     Driven here in the rAF loop via refs so node positions are preserved
    //     (changing the `opacity` prop would re-init them). ---
    if (activeRef.current) {
      if (activeStartTimeRef.current === null) {
        activeStartTimeRef.current = timestamp;
      }
      const activeElapsed = timestamp - activeStartTimeRef.current;
      const rawActive = Math.min(activeElapsed / ACTIVE_RAMP_MS, 1);
      activeProgressRef.current = 1 - Math.pow(1 - rawActive, 3); // ease-out
    } else if (activeProgressRef.current !== 0) {
      // Deactivated (e.g. returned to the login page) — reset so the next
      // activation ramps up from the resting state again.
      activeProgressRef.current = 0;
      activeStartTimeRef.current = null;
    }

    const tp = transitionProgressRef.current;
    const ap = activeProgressRef.current;
    const cp = chargeProgressRef.current;

    // --- Background: interpolate toward dashboard white (#f9fafb) ---
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, lerpHexToWhite(currentBgStops[0], tp));
    gradient.addColorStop(0.5, lerpHexToWhite(currentBgStops[1], tp));
    gradient.addColorStop(1, lerpHexToWhite(currentBgStops[2], tp));
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Effective opacity chain: resting → ACTIVE_OPACITY while loading (ap) →
    // CHARGE_OPACITY during the wind-up (cp) → 1.0 through the bloom (tp). When
    // idle (ap = cp = tp = 0) this is just the resting `opacity`, so nothing
    // changes outside the login flow.
    const idleOpacity = lerp(opacity, Math.max(opacity, ACTIVE_OPACITY), ap);
    const chargedOpacity = lerp(idleOpacity, Math.max(idleOpacity, CHARGE_OPACITY), cp);
    const effectiveOpacity = lerp(chargedOpacity, 1.0, tp);

    // Node scale: a touch larger while active (energy), then the big ease-in
    // swell during the bloom.
    const nodeScale = 1 + ap * ACTIVE_NODE_SCALE + tp * tp * 18;

    // Camera zoom — the "dive into the network". It starts the instant the
    // network activates on login (activeStartTimeRef), so it does NOT wait for
    // the login card/overlay fade or the data load, and smoothsteps to ZOOM_MAX
    // over ZOOM_DIVE_MS, holding there until the bloom whites out. Smoothstep
    // keeps the motion smooth end-to-end (no lurch, no hitch). Applied as a
    // canvas transform below, so the frame stays full of nodes.
    const revealElapsed =
      activeStartTimeRef.current !== null ? timestamp - activeStartTimeRef.current : 0;
    const dive = Math.min(revealElapsed / ZOOM_DIVE_MS, 1);
    const zoomEase = dive * dive * (3 - 2 * dive); // smoothstep
    const zoom = lerp(1, ZOOM_MAX, zoomEase);

    // Colors shift toward white
    const nodeRgb = lerpHexToWhiteRgb(currentColors.node, tp);
    const glowRgb = lerpHexToWhiteRgb(currentColors.nodeGlow, tp);
    const pulseRgb = lerpHexToWhiteRgb(currentColors.pulse, tp);

    const nodes = nodesRef.current;
    const isTransition = tp > 0;

    // --- Draw connections (behind nodes) ---
    // During heavy transition (tp > 0.4), skip triangles and glow layer
    // to keep frame rate smooth with 400 nodes.
    const connectionRange = lerp(200, 300, tp);
    const skipExpensiveEffects = tp > 0.3;

    // 3D fly-in parallax: rather than one uniform camera scale (which looks like
    // zooming a flat image), each node is projected outward from the center by a
    // per-node factor from its depth — nearer nodes spread and grow faster than
    // far ones, so we appear to move *through* a 3D cloud. Physics stays in base
    // coords; only the drawn position (dx, dy) and size factor (dz) change. When
    // idle (zoom === 1) dz === 1, so the resting network is unchanged.
    const centerX = width / 2;
    const centerY = height / 2;
    const motionFactor = 1 + ap * ACTIVE_MOTION_BOOST;

    nodes.forEach((node) => {
      node.x += node.vx * motionFactor;
      node.y += node.vy * motionFactor;

      if (node.x <= 0 || node.x >= width) node.vx *= -1;
      if (node.y <= 0 || node.y >= height) node.vy *= -1;
      node.x = Math.max(0, Math.min(width, node.x));
      node.y = Math.max(0, Math.min(height, node.y));

      node.pulsePhase += 0.02 * animationSpeed * (1 + ap * ACTIVE_PULSE_BOOST);

      node.dz = 1 + (zoom - 1) * lerp(DEPTH_FAR, DEPTH_NEAR, node.depth);
      node.dx = centerX + (node.x - centerX) * node.dz;
      node.dy = centerY + (node.y - centerY) * node.dz;
    });

    // --- Draw connections (behind nodes), at the parallaxed positions ---
    nodes.forEach((node, i) => {
      node.connections.forEach((connectionIndex) => {
        const connectedNode = nodes[connectionIndex];
        if (!connectedNode) return;

        // Existence + fade use the true (base) distance so the graph structure
        // stays stable; only the drawn endpoints are parallaxed.
        const dx = node.x - connectedNode.x;
        const dy = node.y - connectedNode.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < connectionRange) {
          const alpha = Math.max(0, 1 - distance / connectionRange) * effectiveOpacity;
          // Nearer links read heavier — scale line weight by the closer endpoint's depth.
          const lineDepth = Math.max(node.dz, connectedNode.dz);

          ctx.beginPath();
          ctx.moveTo(node.dx, node.dy);
          ctx.lineTo(connectedNode.dx, connectedNode.dy);
          ctx.strokeStyle = `rgba(${nodeRgb}, ${(alpha * 0.8).toFixed(3)})`;
          ctx.lineWidth = lerp(0.8, 2, tp) * lineDepth;
          ctx.stroke();

          if (!skipExpensiveEffects) {
            // Glow connection layer (skipped when nodes are large)
            ctx.beginPath();
            ctx.moveTo(node.dx, node.dy);
            ctx.lineTo(connectedNode.dx, connectedNode.dy);
            ctx.strokeStyle = `rgba(${glowRgb}, ${(alpha * 0.3).toFixed(3)})`;
            ctx.lineWidth = 2 * lineDepth;
            ctx.stroke();

            // Flowing particles
            const flowPosition = (timestamp * 0.0008 * animationSpeed + i * 0.1) % 1;
            const flowX = node.dx + (connectedNode.dx - node.dx) * flowPosition;
            const flowY = node.dy + (connectedNode.dy - node.dy) * flowPosition;

            ctx.beginPath();
            ctx.arc(flowX, flowY, 1.2, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${pulseRgb}, ${(alpha * 0.7).toFixed(3)})`;
            ctx.fill();
          }
        }
      });

      // Triangular fills — only when not transitioning (expensive with many nodes)
      if (!skipExpensiveEffects && node.connections.length >= 2) {
        for (let j = 0; j < node.connections.length - 1; j++) {
          const node1 = nodes[node.connections[j]];
          const node2 = nodes[node.connections[j + 1]];
          if (!node1 || !node2) continue;

          const d1 = Math.sqrt(Math.pow(node.x - node1.x, 2) + Math.pow(node.y - node1.y, 2));
          const d2 = Math.sqrt(Math.pow(node.x - node2.x, 2) + Math.pow(node.y - node2.y, 2));
          const d3 = Math.sqrt(Math.pow(node1.x - node2.x, 2) + Math.pow(node1.y - node2.y, 2));

          if (d1 < 200 && d2 < 200 && d3 < 200) {
            ctx.beginPath();
            ctx.moveTo(node.dx, node.dy);
            ctx.lineTo(node1.dx, node1.dy);
            ctx.lineTo(node2.dx, node2.dy);
            ctx.closePath();
            ctx.fillStyle = `rgba(${nodeRgb}, ${(0.06 * effectiveOpacity).toFixed(3)})`;
            ctx.fill();
          }
        }
      }
    });

    // --- Draw nodes on top, at their parallaxed positions and sizes ---
    nodes.forEach((node) => {
      const pulseIntensity = Math.sin(node.pulsePhase) * 0.2 + 0.8;
      // Scale radius by the node's depth zoom (node.dz) so nearer nodes are
      // larger — the size cue that, with the differential spread, sells the depth.
      const currentRadius = node.radius * pulseIntensity * nodeScale * node.dz;

      if (skipExpensiveEffects) {
        // FAST PATH: simple soft circle instead of radial gradient.
        // A single filled circle with alpha creates the glow-merge look
        // without the cost of creating a CanvasGradient per node per frame.
        const softRadius = currentRadius * 3;
        ctx.beginPath();
        ctx.arc(node.dx, node.dy, softRadius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${glowRgb}, ${(effectiveOpacity * 0.15).toFixed(3)})`;
        ctx.fill();

        // Core node
        ctx.beginPath();
        ctx.arc(node.dx, node.dy, currentRadius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${nodeRgb}, ${(pulseIntensity * effectiveOpacity).toFixed(3)})`;
        ctx.fill();

        // Bright center
        ctx.beginPath();
        ctx.arc(node.dx, node.dy, currentRadius * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${pulseRgb}, 0.67)`;
        ctx.fill();
      } else {
        // QUALITY PATH: full radial gradient glow (normal idle rendering).
        const glowRadius = currentRadius * 4;
        const nodeHex = lerpHexToWhite(currentColors.node, tp);
        const nodeGlowHex = lerpHexToWhite(currentColors.nodeGlow, tp);
        const pulseHex = lerpHexToWhite(currentColors.pulse, tp);

        const glowGradient = ctx.createRadialGradient(
          node.dx, node.dy, 0,
          node.dx, node.dy, glowRadius
        );
        glowGradient.addColorStop(0, nodeGlowHex + "60");
        glowGradient.addColorStop(0.5, nodeGlowHex + "20");
        glowGradient.addColorStop(1, "transparent");

        ctx.beginPath();
        ctx.arc(node.dx, node.dy, glowRadius, 0, Math.PI * 2);
        ctx.fillStyle = glowGradient;
        ctx.fill();

        // Main node
        ctx.beginPath();
        ctx.arc(node.dx, node.dy, currentRadius, 0, Math.PI * 2);
        ctx.fillStyle = nodeHex + Math.floor(pulseIntensity * effectiveOpacity * 255)
          .toString(16).padStart(2, "0");
        ctx.fill();

        // Bright center core
        ctx.beginPath();
        ctx.arc(node.dx, node.dy, currentRadius * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = pulseHex + "AA";
        ctx.fill();

        // Inner highlight for 3D effect
        ctx.beginPath();
        ctx.arc(
          node.dx - currentRadius * 0.2,
          node.dy - currentRadius * 0.2,
          currentRadius * 0.3,
          0, Math.PI * 2
        );
        ctx.fillStyle = "#ffffff40";
        ctx.fill();
      }
    });

    animationRef.current = requestAnimationFrame(animate);
  };

  // Initialize and start animation
  useEffect(() => {
    updateCanvasSize();

    const handleResize = () => {
      updateCanvasSize();
      if (dimensions.width > 0 && dimensions.height > 0) {
        nodesRef.current = initializeNodes(dimensions.width, dimensions.height);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (dimensions.width > 0 && dimensions.height > 0) {
      nodesRef.current = initializeNodes(dimensions.width, dimensions.height);

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      animationRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [dimensions, theme, nodeCount, animationSpeed, opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        position: "fixed",
        zIndex: "-10",
      }}
    />
  );
};

export default NeuralNetworkBackground;
